import { useQuery } from '@tanstack/react-query';
import { handleApiError, api } from '@/lib/api';
import { type CurrentUser } from '@/types/user';
import { usePrivy } from '@privy-io/react-auth';

// Query keys for consistent cache management
export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...userQueryKeys.lists(), filters] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...userQueryKeys.details(), id] as const,
  profile: () => [...userQueryKeys.all, 'profile'] as const,
  current: () => ['currentUser'] as const,
};

// Get current authenticated user from backend
export function useCurrentUser() {
  const { authenticated, user: privyUser } = usePrivy();


  const query = useQuery({
    queryKey: userQueryKeys.current(),
    queryFn: async (): Promise<CurrentUser> => {
      try {
        const userData = await api.get<CurrentUser>('/admin/me');
        return userData;
      } catch (error) {
        handleApiError(error, { context: 'current_user' });
        throw error;
      }
    },
    enabled: authenticated && !!privyUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false,
    // Retry logic is handled globally in QueryProvider
  });

  return query;
}
